import React, { useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, message, Select, Typography, Checkbox, Modal } from 'antd';
import { ApplicationUIContext, Form } from '@yuhuinc/ui-library';

import { getTranslatedString } from 'helpers/i18n';
import { apiPost } from 'helpers/ApiHelper';

import { CurrentApplicationContext } from '../../ApplicationOverview';

const requiredField = {
  required: true,
  message: getTranslatedString('requiredField'),
  validateTrigger: 'onChange',
};

const CancelApplicationForm = ({ afterSubmit }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const currentApplication = useContext(CurrentApplicationContext);
  const queryClient = useQueryClient();

  const cancelApplicationMutation = useMutation({
    mutationFn: (data) => {
      return apiPost(
        `/landlords/application_management/applications/${currentApplication.id}/cancel`,
        data
      );
    },
    onSuccess() {
      let messageText = getTranslatedString(
        'landlordPortal.applicationManagement.applicationOverview.CancelApplication.drawer.success'
      );

      if (values?.is_stale) {
        messageText = getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.CancelApplication.drawer.success.asStale'
        );
      }

      message.success(messageText);

      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
    onError() {
      // TODO: Ask Product for a proper error message
      message.error('Error at cancelling');
    },
    onSettled() {
      afterSubmit();
    },
  });

  const possibleReasons = (currentApplication?.property?.lease_cancellation_reasons || []).map(
    (reason) => ({
      label: reason,
      value: reason,
    })
  );

  return (
    <Form
      form={form}
      layout="vertical"
      size={isMobile && 'large'}
      gap="middle"
      onFinish={cancelApplicationMutation.mutate}
    >
      <Typography.Text>
        {getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.CancelApplication.drawer.text'
        )}
      </Typography.Text>
      <Form.Item name="is_stale" valuePropName="checked">
        <Checkbox>
          {getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.CancelApplication.drawer.application_is_stale'
          )}
        </Checkbox>
      </Form.Item>
      <Form.Item
        label={getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.CancelApplication.drawer.cancellation_reason'
        )}
        name="cancellation_reason"
        rules={[!values?.is_stale && requiredField].filter(Boolean)}
        extra={getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.CancelApplication.drawer.disclaimer'
        )}
      >
        <Select options={possibleReasons} disabled={values?.is_stale} />
      </Form.Item>
      <Button
        type="primary"
        htmlType="submit"
        disabled={!values?.cancellation_reason && !values?.is_stale}
        loading={cancelApplicationMutation.isLoading}
      >
        {getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.CancelApplication.drawer.submit'
        )}
      </Button>
    </Form>
  );
};

export default CancelApplicationForm;
